// src/utils/drawEllipse.js

/**
 * Draw an ellipse defined by three points (P1, P2, P3) where P1 and P2 are assumed
 * to lie on the major axis. P3 defines the height (minor axis radius) of the ellipse.
 *
 * points: [{x,y}, {x,y}, {x,y}]
 * ctx: CanvasRenderingContext2D
 * color: stroke color
 * scale: current transformation scale (used for line width)
 */
export function getEllipseParams(points) {
  if (!points || points.length < 3) return null;
  const [p1, p2, p3] = points;

  // --- 1. Geometric Setup based on constraints ---

  // Center (C): Midpoint of P1 and P2
  const centerX = (p1.x + p2.x) / 2;
  const centerY = (p1.y + p2.y) / 2;
  const C = { x: centerX, y: centerY };

  // Major Axis Radius (a): Half the distance between P1 and P2
  const dx_p1p2 = p2.x - p1.x;
  const dy_p1p2 = p2.y - p1.y;
  const majorRadius_a = Math.hypot(dx_p1p2, dy_p1p2) / 2;

  // Rotation (rotation): Angle of the line P1-P2
  const rotation = Math.atan2(dy_p1p2, dx_p1p2);

  // --- 2. Solve for Minor Radius (b) using P3 ---

  // Shift P3 to the coordinate system centered at C and rotated by -rotation
  const px = p3.x - C.x;
  const py = p3.y - C.y;

  const cos = Math.cos(-rotation);
  const sin = Math.sin(-rotation);

  const xp = px * cos - py * sin;
  const yp = px * sin + py * cos;

  if (Math.abs(majorRadius_a) < 1e-12) return null;

  const term = 1 - (xp * xp) / (majorRadius_a * majorRadius_a);

  if (term <= 1e-12) {
    const minorRadius_b = Math.abs(yp);
    if (minorRadius_b < 1e-12) return null;
    return { centerX, centerY, majorRadius_a, minorRadius_b, rotation }; // Approximate or partial result
  }

  const minorRadius_b = Math.sqrt((yp * yp) / term);

  return { centerX, centerY, majorRadius_a, minorRadius_b, rotation };
}

/**
 * Draw an ellipse defined by three points (P1, P2, P3) where P1 and P2 are assumed
 * to lie on the major axis. P3 defines the height (minor axis radius) of the ellipse.
 *
 * points: [{x,y}, {x,y}, {x,y}]
 * ctx: CanvasRenderingContext2D
 * color: stroke color
 * scale: current transformation scale (used for line width)
 */
export function drawEllipse(ctx, points, color = 'blue', scale = 1) {
  const params = getEllipseParams(points);
  if (!params) return;

  const { centerX, centerY, majorRadius_a, minorRadius_b, rotation } = params;

  // --- 3. Draw the Ellipse and the Points ---

  // Draw points (re-draw here to ensure they are on top of the ellipse)
  const drawPoint = (x, y) => {
    ctx.beginPath();
    // Inversely scale radius to keep point size constant on screen
    const r = 4 / scale;
    ctx.arc(x, y, r, 0, 2 * Math.PI);
    ctx.fillStyle = 'red';
    ctx.fill();
  };

  ctx.beginPath();
  // ctx.ellipse signature: x, y, radiusX, radiusY, rotation, startAngle, endAngle
  ctx.ellipse(
    centerX,
    centerY,
    majorRadius_a,
    minorRadius_b,
    rotation,
    0,
    2 * Math.PI
  );

  ctx.strokeStyle = color;
  // Line width must be inversely scaled to look constant on screen
  ctx.lineWidth = 2 / scale;
  ctx.stroke();

  // drawPoint(p1.x, p1.y);
  // drawPoint(p2.x, p2.y);
  // drawPoint(p3.x, p3.y);
}